import { layoutGraph } from "@components/campaigns/detail/lineage/dagreLayout";
import { StaleBadge } from "@components/campaigns/detail/lineage/StaleBadge";
import { VersionChip } from "@components/campaigns/detail/lineage/VersionChip";
import { OriginBadge } from "@components/shared/OriginBadge";
import {
  Background,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeMouseHandler,
  type NodeProps,
  Position,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useMemo } from "react";
import type {
  NotIncludedItemData,
  ProvenanceGraphData,
  ProvenanceNodeData,
} from "./HowDeterminedPanel";

interface ProvenanceGraphProps {
  provenance: ProvenanceGraphData;
  campaignId: string;
  campaignCandidateId?: string;
  onNavigate?: (path: string) => void;
}

interface ProvenanceFlowNodeData extends Record<string, unknown> {
  node: ProvenanceNodeData | null;
  ghost: boolean;
  ghostItem?: NotIncludedItemData;
  label: string;
}

function styleForEdge(edge: {
  kind: string;
  state: string;
  ghost?: boolean;
}): Pick<Edge, "type" | "animated" | "style" | "markerEnd"> {
  if (edge.ghost) {
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "#94a3b8", strokeWidth: 1.25, strokeDasharray: "5 5", opacity: 0.6 },
    };
  }
  if (edge.state === "STALE") {
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "#dc2626", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#dc2626" },
    };
  }
  if (edge.kind === "EVIDENCE") {
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "#16a34a", strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#16a34a" },
    };
  }
  return {
    type: "smoothstep",
    animated: false,
    style: { stroke: "var(--color-border-strong, #94a3b8)", strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: "var(--color-border-strong, #94a3b8)" },
  };
}

function ProvenanceNodeComponent({ data }: NodeProps<Node<ProvenanceFlowNodeData>>) {
  const ghost = data.ghost;
  const node = data.node;
  const ghostItem = data.ghostItem;

  return (
    <div
      className={`w-[240px] rounded-lg border p-3 shadow-sm ${
        ghost
          ? "border-dashed border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/10"
          : "border-border bg-card"
      }`}
      data-testid={ghost ? "provenance-ghost-node" : "provenance-node"}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        style={{ opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
      />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-text-primary">
            {ghost ? ghostItem?.label : node?.label}
          </div>
          {(ghost ? ghostItem?.subLabel : node?.subLabel) && (
            <div className="truncate text-xs text-text-muted">
              {ghost ? ghostItem?.subLabel : node?.subLabel}
            </div>
          )}
        </div>
        {!ghost && node?.versionNumber != null && (
          <VersionChip versionNumber={node.versionNumber} />
        )}
      </div>
      {!ghost && node && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {(node.origin || node.actorSource) && (
            <OriginBadge origin={node.origin} actorSource={node.actorSource} />
          )}
          {node.stale && <StaleBadge />}
        </div>
      )}
      {ghost && ghostItem && (
        <div className="mt-1.5">
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            Not included
          </span>
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
      />
    </div>
  );
}

const nodeTypes = { provenance: ProvenanceNodeComponent };

export function ProvenanceGraph({
  provenance,
  campaignId,
  campaignCandidateId,
  onNavigate,
}: ProvenanceGraphProps) {
  const { nodes, edges } = useMemo(() => {
    const produced = provenance.nodes.find((n) => n.inputRole == null);
    const producedId = produced?.id ?? "produced";

    const flowNodes: Node<ProvenanceFlowNodeData>[] = provenance.nodes.map((n) => ({
      id: n.id,
      type: "provenance" as const,
      data: { node: n, ghost: false, label: n.label },
      position: { x: 0, y: 0 },
    }));

    const flowEdges: Edge[] = provenance.edges.map((e) => ({
      id: e.id,
      source: e.from,
      target: e.to,
      ...styleForEdge(e),
    }));

    for (const item of provenance.notIncluded) {
      const ghostId = `ghost:${item.artefactType}:${item.artefactId}`;
      flowNodes.push({
        id: ghostId,
        type: "provenance" as const,
        data: { node: null, ghost: true, ghostItem: item, label: item.label },
        position: { x: 0, y: 0 },
      });
      flowEdges.push({
        id: `ghost-edge:${ghostId}`,
        source: ghostId,
        target: producedId,
        ...styleForEdge({ kind: "INPUT", state: "CURRENT", ghost: true }),
      });
    }

    return { nodes: layoutGraph(flowNodes, flowEdges, { rankdir: "TB" }), edges: flowEdges };
  }, [provenance]);

  const handleNodeClick: NodeMouseHandler = (_event, node) => {
    const data = node.data as ProvenanceFlowNodeData;
    if (data.ghost || !data.node || !onNavigate) return;
    const path = provenanceNodeLink(data.node, campaignId, campaignCandidateId);
    if (path) onNavigate(path);
  };

  return (
    <div
      className="h-[500px] w-full rounded-lg border border-border bg-surface"
      data-testid="provenance-graph"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        edgesReconnectable={false}
        elementsSelectable
        panOnDrag
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

function provenanceNodeLink(
  node: ProvenanceNodeData,
  campaignId: string,
  campaignCandidateId?: string,
): string | null {
  switch (node.artefactType) {
    case "RoleBlueprintVersion":
      return `/campaigns/${campaignId}/define/blueprint`;
    case "CampaignBriefVersion":
      return `/campaigns/${campaignId}/define/brief`;
    case "AssessmentMatrixVersion":
    case "AssessmentMatrixBand":
      return `/campaigns/${campaignId}/define/assessment`;
    case "StepAssessmentPlanVersion":
    case "SelectionStep":
      return `/campaigns/${campaignId}/define/selection-process`;
    case "InterviewContribution":
      return campaignCandidateId
        ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}#contributions`
        : null;
    case "CandidateAssessmentVersion":
      return campaignCandidateId
        ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}#assessment`
        : null;
    case "InterviewRoundSynthesisVersion":
    case "CandidateSelectionStep":
      return campaignCandidateId
        ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}`
        : null;
    default:
      return null;
  }
}
